/** W82 Teil Web-2: Lage des Haushalts für die Startseite (plan-w82.md 3.6, E10 bis E12). Reine Funktionen, gerechnet in
 *  Europe/Berlin wie der Server und iOS; dieselben Fälle stehen in iOS `WeltLage.swift` und in beiden Tests. Die Akten
 *  (Web-3) nehmen `fristZustand` und `dringlichkeit` für „Dringend zuerst“. */
import type { Aufgabe, Frist } from "../api/typen";
import { datumText } from "./datum";

export type Zustand = "ueberfaellig" | "bald" | "spaeter" | "erledigt";

const berlin = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" });

/** Kalendertag „heute“ in Europe/Berlin als JJJJ-MM-TT (nicht in UTC: am 25.10.2026 um 00:30 ist in UTC noch der 24.). */
export function heuteBerlin(jetzt: Date = new Date()): string {
  return berlin.format(jetzt);
}

/** Tagesnummer eines ISO-Tags, ohne Uhrzeit und Sommerzeit (UTC-Mitternacht). */
function tagNr(iso: string): number {
  const [j, m, t] = iso.slice(0, 10).split("-").map(Number);
  return Math.round(Date.UTC(j ?? 1970, (m ?? 1) - 1, t ?? 1) / 86_400_000);
}

/** ISO-Tag plus n Tage. */
export function plusTage(iso: string, n: number): string {
  return new Date((tagNr(iso) + n) * 86_400_000).toISOString().slice(0, 10);
}

/** Ganze Tage von heute bis zum Tag (negativ = vorbei). */
export function tageBis(iso: string, heute: string): number {
  return tagNr(iso) - tagNr(heute);
}

/** Zustand einer Frist wie die Gruppen der Fristen-Seite: vorbei überfällig, 0 bis 7 Tage bald, sonst später. */
export function fristZustand(f: Pick<Frist, "datum" | "erledigt">, heute: string): Zustand {
  if (f.erledigt) return "erledigt";
  const t = tageBis(f.datum, heute);
  return t < 0 ? "ueberfaellig" : t <= 7 ? "bald" : "spaeter";
}

/** Offene Fristen nach Datum (wie iOS `fristenSortiert`; gleiches Datum nach Kennung, damit die Reihenfolge feststeht). */
export function offenSortiert(fristen: Frist[]): Frist[] {
  return fristen.filter((f) => !f.erledigt).sort((a, b) => a.datum.localeCompare(b.datum) || a.id.localeCompare(b.id));
}

/** E12: die vier Zellen des Offen-Blocks, in beiden Fassungen gleich gerechnet. `fixkosten` ist die Summe im Monat aus
 *  `GET /api/finanzen` (null, solange nichts eingetragen ist). `seit` ist der Tag der ältesten überfälligen, `bis` der
 *  der nächsten baldigen Frist oder Aufgabe (K36: offene Aufgaben mit Fälligkeit zählen wie Fristen, ohne Fälligkeit
 *  oder erledigt nicht; gleich in iOS `WeltLage.swift`). */
export type OffenLage = {
  ueberfaellig: number;
  seit: string | null;
  bald: number;
  bis: string | null;
  pruefen: number;
  fixkosten: number | null;
};

export function offenLage(
  daten: { fristen: Frist[]; aufgaben?: Aufgabe[]; pruefen: number; fixkosten: number | null },
  heute: string,
): OffenLage {
  const tage = [...offenSortiert(daten.fristen).map((f) => f.datum), ...aufgabenTage(daten.aufgaben ?? [])].sort();
  const vorbei = tage.filter((d) => fristZustand({ datum: d, erledigt: null }, heute) === "ueberfaellig");
  const bald = tage.filter((d) => fristZustand({ datum: d, erledigt: null }, heute) === "bald");
  return {
    ueberfaellig: vorbei.length,
    seit: vorbei[0] ?? null,
    bald: bald.length,
    bis: bald[0] ?? null,
    pruefen: daten.pruefen,
    fixkosten: daten.fixkosten && daten.fixkosten > 0 ? daten.fixkosten : null,
  };
}

/** K36: Fälligkeiten der offenen Aufgaben (JJJJ-MM-TT); ohne Fälligkeit oder erledigt zählt eine Aufgabe nicht. */
function aufgabenTage(aufgaben: Aufgabe[]): string[] {
  return aufgaben.flatMap((a) => (a.erledigt || !a.faellig ? [] : [a.faellig.slice(0, 10)]));
}

/** K36: erste offene Aufgabe im Zustand (überfällig: die älteste, bald: die nächste); gleicher Tag nach Kennung. Der
 *  Rundgang nimmt sie als Ziel, wenn sie vor der ersten Frist im selben Zustand liegt. */
export function aufgabeIm(aufgaben: Aufgabe[], zustand: "ueberfaellig" | "bald", heute: string): (Aufgabe & { faellig: string }) | null {
  const passend = aufgaben
    .filter((a): a is Aufgabe & { faellig: string } => !a.erledigt && Boolean(a.faellig))
    .filter((a) => fristZustand({ datum: a.faellig, erledigt: null }, heute) === zustand)
    .sort((a, b) => a.faellig.localeCompare(b.faellig) || a.id.localeCompare(b.id));
  return passend[0] ?? null;
}

/** Was der Geist erklärt (plan-w82.md 3.5): etwas ist überfällig oder ein Vorschlag wartet. */
export function erklaert(lage: OffenLage): boolean {
  return lage.ueberfaellig > 0 || lage.pruefen > 0;
}

/** Dinge, die anstehen: überfällig plus bald plus zu prüfen (Fixkosten zählen nicht). */
export function dinge(lage: OffenLage): number {
  return lage.ueberfaellig + lage.bald + lage.pruefen;
}

/** E11 wie iOS `FristenUebersicht.bald`: alle überfälligen und alle der nächsten 14 Tage, ist das leer, die nächsten drei.
 *  Reihenfolge wie geliefert (der Server sortiert nach Datum; die Liste des Bestands hielt die Reihenfolge der Antwort,
 *  `w80-r1-posteingang.spec.ts`). */
export function fristenBald(fristen: Frist[], heute: string): Frist[] {
  const offen = fristen.filter((f) => !f.erledigt);
  const gewaehlt = offen.filter((f) => tageBis(f.datum, heute) <= 14);
  return gewaehlt.length ? gewaehlt : offen.slice(0, 3);
}

// ---------- Säulen (E10) ----------

/** Höhe der Säule in px, Segment mindestens 8, Lücke 2 (tokens.json `masse.fristen`). */
export const SAEULE = { hoehe: 104, mindestens: 8, luecke: 2, hoechstens: 6 } as const;

export type Segment = {
  /** Kennungen der Fristen darin (zusammengefasst mehrere) */
  ids: string[];
  betrag: number;
  zustand: Zustand;
  hoehe: number;
  /** Art der Frist für die Textfassung (bei Zusammenfassung die der dringendsten) */
  art: string;
};

export type Spalte = {
  art: "vorbei" | "woche";
  /** letzter Tag der Woche (JJJJ-MM-TT); bei „vorbei“ null */
  bis: string | null;
  /** von unten nach oben, dringendste unten */
  segmente: Segment[];
  /** Summe der offenen Beträge (Wochensumme über der Säule; 0 heißt leer) */
  summe: number;
};

const RANG: Record<Zustand, number> = { ueberfaellig: 0, bald: 1, spaeter: 2, erledigt: 3 };

function betragZahl(f: Frist): number {
  const n = Number(f.betrag ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/** Festes Fenster: „vorbei“ (alle offenen überfälligen) und vier Wochen ab heute (heute bis heute plus 6, plus 7 bis 13,
 *  plus 14 bis 20, plus 21 bis 27). Erledigte im Fenster als Segment `erledigt`; spätere stehen nur in der Liste. Mehr als
 *  sechs Fristen in einer Spalte: ab der sechsten werden die kleinsten zusammengefasst (Zustand der dringendsten darin).
 *  Höhe: größter Spaltenbetrag = 104 px, Segment mindestens 8 px, ohne Betrag 8 px. */
export function saeulen(fristen: Frist[], heute: string): Spalte[] {
  const roh: { art: Spalte["art"]; bis: string | null; fristen: Frist[] }[] = [
    { art: "vorbei", bis: null, fristen: fristen.filter((f) => !f.erledigt && tageBis(f.datum, heute) < 0) },
    ...[0, 1, 2, 3].map((w) => ({
      art: "woche" as const,
      bis: plusTage(heute, w * 7 + 6),
      fristen: fristen.filter((f) => {
        const t = tageBis(f.datum, heute);
        return t >= w * 7 && t <= w * 7 + 6;
      }),
    })),
  ];
  const sortiert = roh.map((s) => ({
    ...s,
    fristen: [...s.fristen].sort(
      (a, b) => RANG[fristZustand(a, heute)] - RANG[fristZustand(b, heute)] || a.datum.localeCompare(b.datum) || a.id.localeCompare(b.id),
    ),
  }));
  const spaltenBetrag = (fs: Frist[]) => fs.reduce((s, f) => s + betragZahl(f), 0);
  const hoechster = Math.max(0, ...sortiert.map((s) => spaltenBetrag(s.fristen)));
  return sortiert.map((s) => {
    let gruppen: Frist[][] = s.fristen.map((f) => [f]);
    if (gruppen.length > SAEULE.hoechstens) {
      // die fünf größten bleiben, der Rest (die kleinsten) wird ein Segment; Reihenfolge danach wie vorher
      const nachBetrag = [...s.fristen].sort((a, b) => betragZahl(b) - betragZahl(a) || a.id.localeCompare(b.id));
      const bleiben = new Set(nachBetrag.slice(0, SAEULE.hoechstens - 1).map((f) => f.id));
      const rest = s.fristen.filter((f) => !bleiben.has(f.id));
      gruppen = [...s.fristen.filter((f) => bleiben.has(f.id)).map((f) => [f]), rest];
    }
    const segmente: Segment[] = gruppen.map((g) => {
      const betrag = Math.round(spaltenBetrag(g) * 100) / 100;
      const erster = g[0] as Frist;
      return {
        ids: g.map((f) => f.id),
        betrag,
        zustand: fristZustand(erster, heute),
        art: erster.art,
        hoehe: betrag > 0 && hoechster > 0 ? Math.max(SAEULE.mindestens, Math.round((betrag / hoechster) * SAEULE.hoehe)) : SAEULE.mindestens,
      };
    });
    // die Säule läuft nie über: reicht der Platz mit Lücken nicht, werden die großen Segmente gestaucht
    const platz = SAEULE.hoehe - SAEULE.luecke * Math.max(0, segmente.length - 1);
    const summeHoehe = segmente.reduce((a, x) => a + x.hoehe, 0);
    if (summeHoehe > platz) {
      const fest = segmente.filter((x) => x.hoehe <= SAEULE.mindestens).length * SAEULE.mindestens;
      const gross = summeHoehe - fest;
      const faktor = gross > 0 ? Math.max(0, platz - fest) / gross : 0;
      for (const x of segmente) if (x.hoehe > SAEULE.mindestens) x.hoehe = Math.max(SAEULE.mindestens, Math.floor(x.hoehe * faktor));
    }
    const summe = Math.round(s.fristen.filter((f) => !f.erledigt).reduce((a, f) => a + betragZahl(f), 0) * 100) / 100;
    return { art: s.art, bis: s.bis, segmente, summe };
  });
}

/** „noch offen“: Summe der offenen Beträge im Fenster. */
export function nochOffen(spalten: Spalte[]): number {
  return Math.round(spalten.reduce((a, s) => a + s.summe, 0) * 100) / 100;
}

/** Ob in einer Spalte etwas überfällig ist (Achse „vorbei“ in `ueberfaelligText`). */
export function brennt(spalten: Spalte[]): boolean {
  return spalten.some((s) => s.segmente.some((x) => x.zustand === "ueberfaellig"));
}

/** Art der Frist für die Textfassung („Zahlung, 24,99 Euro, überfällig“). */
const ART_TEXT: Record<string, string> = { zahlen: "Zahlung", widersprechen: "Widerspruch", kuendigen: "Kündigung", melden: "Meldung", sonstige: "Frist" };
const ZUSTAND_TEXT: Record<Zustand, string> = { ueberfaellig: "überfällig", bald: "bald", spaeter: "später", erledigt: "erledigt" };

const euroSprache = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** „TT.MM.“ eines vergangenen ISO-Tags („seit …“, „offen seit …“; datum.ts Form `vergangen`). */
export function tagMonat(iso: string): string {
  return datumText(iso, "vergangen");
}

/** Textfassung des Diagramms für Vorlesen: „Vorbei: Zahlung, 24,99 Euro, überfällig. Bis 22.10: Zahlung, 112,80 Euro,
 *  bald.“ Leere Spalten fallen weg. W82 Web-5h-N2b-2 (b-HW23): die Spalten reichen bis zu künftigen Tagen, Form `kurz`. */
export function diagrammText(spalten: Spalte[]): string {
  return spalten
    .filter((s) => s.segmente.length)
    .map((s) => {
      const kopf = s.art === "vorbei" ? "Vorbei" : `Bis ${datumText(s.bis as string, "kurz")}`;
      const teile = s.segmente.map((x) =>
        [ART_TEXT[x.art] ?? "Frist", x.betrag > 0 ? `${euroSprache.format(x.betrag)} Euro` : null, ZUSTAND_TEXT[x.zustand]].filter(Boolean).join(", "),
      );
      return `${kopf}: ${teile.join("; ")}.`;
    })
    .join(" ");
}

// ---------- Für die Akten (Web-3, FW4 „Dringend zuerst“) ----------

/** Rang einer Akte nach E15: überfällig (0) vor bald (1, Frist oder Kündigung in 0 bis 7 Tagen) vor später (2, andere
 *  offene Frist) vor Ruhe (3). Gleicher Rang ordnet der Aufrufer nach Namen. */
export function dringlichkeit(akte: { id: string; ueberfaellig?: boolean; kuendigungBis?: string | null }, fristen: Frist[], heute: string): number {
  const eigene = fristen.filter((f) => !f.erledigt && f.akte_id === akte.id);
  if (akte.ueberfaellig || eigene.some((f) => tageBis(f.datum, heute) < 0)) return 0;
  const bald = (iso: string) => {
    const t = tageBis(iso, heute);
    return t >= 0 && t <= 7;
  };
  if (eigene.some((f) => bald(f.datum)) || (akte.kuendigungBis && bald(akte.kuendigungBis))) return 1;
  return eigene.length ? 2 : 3;
}
